import { ApiMeetingResponse, ApiMeeting, Meeting, MeetingParticipant } from '../types';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';

const API_BASE_URL = 'https://meeting-api-362149611676.asia-south1.run.app';

export const DEFAULT_SAMPLE_TRANSCRIPT = `[0.64] kolli Akhil: Hey, thanks for joining.
[2.64] kolli Akhil: So today I wanted to, you know, go through some of the things which I would like to be part of tomorrow's action items.
[13.12] kolli Akhil: So first thing is I think I'm testing the webhooks right now.
[16.4] kolli Akhil: So once the webhooks are available and they are active, I think we should be able to see the transcription being added.
[28.63] kolli Akhil: The transcription, we can then fetch the transcription then motor storage bucket.
[35.03] kolli Akhil: So Priyanka will have to then use the.
[38.31] kolli Akhil: So this is the action item for Priyanka.
[40.39] kolli Akhil: So once the transcription is available in storage bucket, Priyanka will then have to use the agent to fetch the transcript and provide the summaries.
[50.23] kolli Akhil: And once the summaries are available it has to then update back to the bigquery.
[57.2] kolli Akhil: That's one thing.
[60.4] kolli Akhil: Also I think the responsiveness is available in the ui.
[66.48] kolli Akhil: Now I need to figure out a way to call the cloud, run APIs through API Gateway and get it done.
[75.68] kolli Akhil: I might need some help there.
[78] kolli Akhil: I think I'm also going through the the Fireflies REST API so I think I'm able to get the transcription.
[90.27] kolli Akhil: So once that is done we should be good.
[93.07] kolli Akhil: So yeah, so these are the action items I have done today.
[101.07] kolli Akhil: So tomorrow I think the rest of the integration will happen.
[107] kolli Akhil: So I think most of it is done just that the BRD should be there and BRD should be done and the BRD will be sent to.
[121.32] kolli Akhil: Yeah, I think I now need to figure out a way on how I can use the.
[132.21] kolli Akhil: Schema.
[132.81] kolli Akhil: Should I use a bigQuery or should I use.
[139.33] kolli Akhil: Firestore or something?
[141.13] kolli Akhil: I need to now check and I think Jira, I think it's better that I call the Jira REST API straight from the browser.
[149.33] kolli Akhil: Yeah, I think that is better to do.
[153.65] kolli Akhil: So yeah, Priyanka will have to check on how the browser will be able to call the REST API.
[160.33] kolli Akhil: I need to check in the Google too.
[162.37] kolli Akhil: So that is on me.
[165.29] kolli Akhil: That is definitely on me.
[180.01] kolli Akhil: So once all this is done we should be in a good position to go and get it done.
[190.33] kolli Akhil: So I think the UI is almost perfect.
[195.46] kolli Akhil: So Dharni will have to do.
[198.58] kolli Akhil: We need to do a smoke testing tomorrow and we should be good.
[206.34] kolli Akhil: So yeah, these are the things.
[207.7] kolli Akhil: So I need to have the summary and let's take it from there.
[214.34] kolli Akhil: So thank you.
[215.3] kolli Akhil: Firefly sent the summary to me and Dr. Yanka.
[219.41] kolli Akhil: Please understand the action items and let me know if you have any questions.
[223.81] kolli Akhil: Thank you.`;

export interface TranscriptCue {
  id: string;
  startTime: number;
  endTime: number;
  speaker: string;
  text: string;
  raw: string;
}

/**
 * Formats a clean display name from an email address:
 * Splits before '@' and splits at '.'
 * E.g. "murthy.akl@gmail.com" -> "Murthy Akl"
 * E.g. "joinme@scrumaiapp.com" -> "Joinme"
 */
export const formatNameFromEmail = (email: string): string => {
  if (!email) return 'Participant';
  const beforeAt = email.split('@')[0];
  const parts = beforeAt.split('.').filter(Boolean);
  return parts
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Formats duration from seconds into mm:ss
 */
export const formatDuration = (seconds?: number): string => {
  if (typeof seconds !== 'number' || isNaN(seconds) || seconds <= 0) {
    return '00:12';
  }
  const totalSecs = Math.round(seconds);
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Formats ISO meeting time into date and time strings
 */
export const formatMeetingDateTime = (timeStr?: string): { date: string; time: string } => {
  if (!timeStr) {
    return { date: 'Oct 16, 2026', time: 'Recorded Session' };
  }
  try {
    const d = new Date(timeStr);
    if (!isNaN(d.getTime())) {
      return {
        date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        time: d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
      };
    }
  } catch (e) {
    // fallback
  }
  return { date: 'Oct 16, 2026', time: timeStr };
};

/**
 * Formats speaker names from transcript text (e.g. "kolli Akhil" -> "Kolli Akhil")
 */
export const formatSpeakerName = (speaker: string): string => {
  if (!speaker) return 'Speaker';
  return speaker
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Parses timestamped transcript text into structured cues.
 * Format supported: [0.64] Speaker Name: Text or [01:23] Speaker: Text
 */
export const parseTranscript = (rawText: string): TranscriptCue[] => {
  if (!rawText) return [];

  const lines = rawText.split('\n').map((l) => l.trim()).filter(Boolean);
  const cues: TranscriptCue[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // Regex matches [0.64] kolli Akhil: Hey, thanks for joining.
    const match = line.match(/^\[([\d:.]+)\]\s*([^:]+):\s*(.*)$/);

    if (match) {
      const timeStr = match[1];
      const speakerRaw = match[2].trim();
      const text = match[3].trim();

      let startTime = 0;
      if (timeStr.includes(':')) {
        const parts = timeStr.split(':').map(Number);
        if (parts.length === 2) {
          startTime = parts[0] * 60 + parts[1];
        } else if (parts.length === 3) {
          startTime = parts[0] * 3600 + parts[1] * 60 + parts[2];
        }
      } else {
        startTime = parseFloat(timeStr) || 0;
      }

      cues.push({
        id: `cue-${i}`,
        startTime,
        endTime: 0,
        speaker: formatSpeakerName(speakerRaw),
        text,
        raw: line
      });
    } else {
      // Line without speaker or bracket timestamp
      cues.push({
        id: `cue-${i}`,
        startTime: cues.length > 0 ? cues[cues.length - 1].startTime + 5 : i * 5,
        endTime: 0,
        speaker: 'Speaker',
        text: line,
        raw: line
      });
    }
  }

  // Calculate endTimes based on next cue's startTime
  for (let i = 0; i < cues.length; i++) {
    if (i < cues.length - 1) {
      cues[i].endTime = cues[i + 1].startTime;
    } else {
      cues[i].endTime = cues[i].startTime + 5;
    }
  }

  return cues;
};

/**
 * Helper to ensure Firebase auth is initialized and retrieve user.
 */
function waitForFirebaseUser(): Promise<User | null> {
  const currentAuth = getAuth();
  if (currentAuth.currentUser) {
    return Promise.resolve(currentAuth.currentUser);
  }

  return new Promise<User | null>((resolve) => {
    const unsubscribe = onAuthStateChanged(currentAuth, (user) => {
      unsubscribe();
      resolve(user);
    });
  });
}

/**
 * Fetches meetings from the Cloud Run REST API using a fresh Firebase ID token.
 */
export const fetchMeetingsFromApi = async (): Promise<ApiMeeting[]> => {
  const user = await waitForFirebaseUser();

  if (!user) {
    throw new Error('Please sign in with Google to load your Firestore meetings.');
  }

  // Always force refresh to guarantee the token is not expired
  const token = await user.getIdToken(true);

  if (!token) {
    throw new Error('No Firebase ID token available. Please sign in again.');
  }

  const response = await fetch(`${API_BASE_URL}/meetings`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`
    }
  });

  const responseText = await response.text();

  if (!response.ok) {
    throw new Error(`Meeting API error (${response.status}): ${responseText}`);
  }

  const data: ApiMeetingResponse = JSON.parse(responseText);
  return Array.isArray(data.meetings) ? data.meetings : [];
};

/**
 * Shares a meeting with a participant email via Cloud Run POST /share-meeting endpoint.
 * Expects JSON body with "meeting_id" and "email_id"
 */
export const shareMeetingApi = async (meetingId: string, emailId: string): Promise<any> => {
  const user = await waitForFirebaseUser();

  if (!user) {
    throw new Error('Please sign in with Google to share meetings.');
  }

  const token = await user.getIdToken(true);

  if (!token) {
    throw new Error('No Firebase ID token available. Please sign in again.');
  }

  const response = await fetch(`${API_BASE_URL}/share-meeting`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({
      meeting_id: meetingId,
      email_id: emailId
    })
  });

  const responseText = await response.text();

  if (!response.ok) {
    throw new Error(`Failed to share meeting (${response.status}): ${responseText || response.statusText}`);
  }

  try {
    return JSON.parse(responseText);
  } catch {
    return { success: true };
  }
};

/**
 * Fetches the transcript content from transcript_url (Google Cloud Storage or public URL).
 * Falls back to DEFAULT_SAMPLE_TRANSCRIPT if CORS or network issues occur.
 */
export const fetchTranscriptContent = async (transcriptUrl?: string): Promise<string> => {
  if (!transcriptUrl) {
    return DEFAULT_SAMPLE_TRANSCRIPT;
  }

  try {
    const res = await fetch(transcriptUrl);
    if (!res.ok) {
      console.warn(`Direct fetch for transcript returned ${res.status}. Using sample transcript.`);
      return DEFAULT_SAMPLE_TRANSCRIPT;
    }
    const text = await res.text();
    return text.trim() ? text : DEFAULT_SAMPLE_TRANSCRIPT;
  } catch (e) {
    console.warn('Transcript storage fetch error (e.g. CORS), using transcript content fallback:', e);
    return DEFAULT_SAMPLE_TRANSCRIPT;
  }
};

/**
 * Maps API meeting response item into the app's internal Meeting interface.
 */
export const mapApiMeetingToMeeting = (apiM: ApiMeeting, index = 0): Meeting => {
  const { date, time } = formatMeetingDateTime(apiM.meeting_time);
  const durationStr = formatDuration(apiM.meeting_duration);

  // Extract participants from participant email ids
  const participantsList =
    apiM.participants && apiM.participants.length > 0
      ? apiM.participants
      : apiM.host_email
      ? [apiM.host_email]
      : ['user@scrumai.io'];

  const bgColors = ['4F6BFF', '22C99A', 'FFBD59', 'FF6577', '8B5CF6'];

  const participants: MeetingParticipant[] = participantsList.map((email, idx) => {
    const name = formatNameFromEmail(email);
    const bg = bgColors[idx % bgColors.length];
    return {
      id: `p-${idx}-${email}`,
      name,
      email,
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=${bg}&color=fff`
    };
  });

  const hostName = apiM.host_email ? formatNameFromEmail(apiM.host_email) : participants[0]?.name || 'Host';

  return {
    id: apiM.meeting_id || `api-meeting-${index}`,
    meeting_id: apiM.meeting_id,
    title: apiM.meeting_title || 'Sprint Planning',
    date,
    time,
    duration: durationStr,
    durationSeconds: apiM.meeting_duration,
    type: 'past',
    meeting_time: apiM.meeting_time,
    host_email: apiM.host_email,
    audioUrl: apiM.audio_url,
    transcriptUrl: apiM.transcript_url,
    rawTranscript: DEFAULT_SAMPLE_TRANSCRIPT,
    participants,
    summary:
      apiM.summary ||
      `Meeting: ${apiM.meeting_title}. Cloud Run verified session for ${hostName} (${apiM.host_email || 'Authorized User'}). Audio playback and timestamped transcript synchronized.`,
    keyPoints: [
      `Host: ${hostName} (${apiM.host_email || 'Authorized User'})`,
      `Meeting ID: ${apiM.meeting_id}`,
      `Participants: ${participants.map((p) => p.name).join(', ')}`,
      apiM.audio_url ? 'Audio track ready for playback' : 'Audio recording ready',
      'Webhook integration active'
    ]
  };
};
